// Bói Đê — Vận khí theo kỳ: Ngày / Tuần / Tháng.
//
// Thứ tự trên tab Ngày (cố ý): chọn kỳ → tờ lịch + điểm → BÀI LUẬN → chi tiết tra cứu
// → tiết khí → lối sang Xem ngày theo việc. Bài luận là phần người xem đến để đọc,
// không được nằm dưới cả bảng tra cứu dài. "Chọn ngày cho một việc" đã thành trang
// riêng `/xem-ngay` (tìm cả khoảng ngày, hướng, hạn năm) nên không còn là tab ở đây.
//
// Kỳ HIỆN TẠI (hôm nay / tuần này / tháng âm này) miễn phí; kỳ khác (đã qua hoặc
// sắp tới) mở bằng số dư; hỏi thêm thầy tính phí theo token. Trạng thái
// (locked/open/closed) do backend quyết định — trang này chỉ hiển thị và đếm
// ngược theo `server_time`, không tự suy ra từ đồng hồ máy người dùng.
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { toast } from "sonner";

import { AlmanacHeader, AlmanacLink } from "@/components/van-khi/almanac-layout";
import { IconVanKhi, IconXemNgay } from "@/components/nav-icons";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getVanKhiReading,
  listVanKhiPeriods,
  openVanKhiReading,
  type VanKhiKind,
  type VanKhiPeriod,
} from "@/lib/boide-api";
import { VanKhiGate } from "@/components/van-khi/van-khi-gate";
import { VanKhiPeriodStrip } from "@/components/van-khi/van-khi-period-strip";
import { VanKhiReadingView } from "@/components/van-khi/van-khi-reading";
import { refreshBoideUser } from "@/hooks/use-boide-user";
import { useServerNow } from "@/components/van-khi/van-khi-countdown";
import { VanKhiLichNgayPanel } from "@/components/van-khi/van-khi-lich-ngay";

type TabKey = VanKhiKind;

const TABS: { key: TabKey; label: string; helper: string }[] = [
  { key: "day", label: "Ngày", helper: "Hôm nay miễn phí · ngày khác mở bằng số dư" },
  { key: "week", label: "Tuần", helper: "Tuần này miễn phí · tuần khác mở bằng số dư" },
  { key: "month", label: "Tháng", helper: "Tháng âm lịch này miễn phí · tháng khác mở bằng số dư" },
];

export const Route = createFileRoute("/_authenticated/tong-hop/van-khi")({
  // `tab=xem-ngay` là link cũ (tab "Chọn ngày" đã thành trang riêng) — giữ để chuyển tiếp.
  validateSearch: (search: Record<string, unknown>): { tab?: TabKey | "xem-ngay" } => ({
    tab:
      search.tab === "xem-ngay" || TABS.some((t) => t.key === search.tab)
        ? (search.tab as TabKey | "xem-ngay")
        : undefined,
  }),
  component: VanKhiPage,
});

function VanKhiPage() {
  const { tab } = Route.useSearch();
  if (tab === "xem-ngay") return <Navigate to="/xem-ngay" replace />;
  const activeTab: TabKey = tab ?? "day";
  const activeMeta = TABS.find((item) => item.key === activeTab) ?? TABS[0];

  return (
    // main + overflow-y-auto: layout _authenticated không tự cuộn, mỗi trang phải
    // tự làm vùng cuộn của mình (giống diem-danh / hop-thu).
    <main className="almanac-page min-h-0 flex-1 overflow-y-auto">
      <div className="almanac-container">
        <AlmanacHeader
          title="Vận khí của bạn"
          description="Xem điều nên làm và nên tránh theo từng thời điểm."
          icon={<IconVanKhi className="size-8" />}
        />

        <nav aria-label="Chọn loại vận khí" className="almanac-tabs">
          {TABS.map((item) => (
            <Link
              key={item.key}
              to="/tong-hop/van-khi"
              search={{ tab: item.key === "day" ? undefined : item.key }}
              activeOptions={{ exact: true, includeSearch: true, explicitUndefined: true }}
              aria-current={item.key === activeTab ? "page" : undefined}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <p className="almanac-period-hint">{activeMeta.helper}</p>

        <PeriodTab key={activeTab} kind={activeTab} />

        <AlmanacLink
          to="/xem-ngay"
          title="Tìm ngày tốt theo việc"
          description="Cưới hỏi, mua xe, khai trương, nhập trạch…"
          icon={<IconXemNgay className="size-6" />}
        />
      </div>
    </main>
  );
}

function PeriodTab({ kind }: { kind: VanKhiKind }) {
  const queryClient = useQueryClient();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const periodsQuery = useQuery({
    queryKey: ["van-khi-periods", kind],
    queryFn: () => listVanKhiPeriods(kind),
    staleTime: 60 * 1000,
  });

  // Memo hoá để `?? []` không tạo mảng mới mỗi lần render (làm vỡ deps của useMemo dưới).
  const periods = useMemo(() => periodsQuery.data?.periods ?? [], [periodsQuery.data]);

  // Mặc định chọn kỳ hiện tại (miễn phí).
  const defaultKey = useMemo(() => {
    if (!periods.length) return null;
    return (periods.find((p) => p.state === "open") ?? periods[0]).key;
  }, [periods]);

  const activeKey = selectedKey ?? defaultKey;

  const readingQuery = useQuery({
    queryKey: ["van-khi-reading", kind, activeKey],
    queryFn: () => getVanKhiReading(kind, activeKey ?? undefined),
    enabled: Boolean(activeKey),
    staleTime: 60 * 1000,
  });

  const openMutation = useMutation({
    mutationFn: () => openVanKhiReading(kind, activeKey as string),
    onSuccess: (result) => {
      queryClient.setQueryData(["van-khi-reading", kind, activeKey], {
        period: result.period,
        reading: result.reading,
        has_reading: true,
        can_open: false,
        can_purchase: false,
        price: 0,
        next_open_at: null,
      });
      void queryClient.invalidateQueries({ queryKey: ["van-khi-periods", kind] });
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Chưa mở được kỳ này");
      // Kỳ có thể vừa chuyển (qua nửa đêm) ngay lúc bấm → đọc lại trạng thái từ server.
      void queryClient.invalidateQueries({ queryKey: ["van-khi-periods", kind] });
      void queryClient.invalidateQueries({ queryKey: ["van-khi-reading", kind, activeKey] });
    },
    // 🔴 Lượt này VỪA TRỪ TIỀN (quota trước, ví bù phần thiếu). Không đọc lại hồ sơ
    // thì số dư và hạn mức trên màn hình đứng im tới khi người dùng F5 — và
    // `refreshBoideUser()` cũng chính là thứ bắn sự kiện cho `useQuotaStatus`.
    // Đặt ở `onSettled`: lỗi SAU khi BE đã trừ tiền là có thật (hoàn tiền, timeout),
    // lúc ấy con số vẫn phải đúng.
    onSettled: () => void refreshBoideUser(),
  });

  const serverNow = useServerNow(periodsQuery.data?.server_time);

  if (periodsQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full rounded-2xl" />
        <Skeleton className="h-56 w-full rounded-3xl" />
      </div>
    );
  }

  if (periodsQuery.isError) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Không tải được các kỳ vận khí. Thử lại sau nhé.
      </p>
    );
  }

  const activeIndex = periods.findIndex((p) => p.key === activeKey);
  const previous: VanKhiPeriod | undefined = activeIndex > 0 ? periods[activeIndex - 1] : undefined;

  return (
    <div className="space-y-5">
      <VanKhiPeriodStrip
        kind={kind}
        periods={periods}
        selectedKey={activeKey}
        onSelect={(period) => setSelectedKey(period.key)}
      />

      {/* Tờ lịch + điểm (miễn phí) đứng đầu: đọc điểm trước, đọc lời sau. */}
      {kind === "day" && activeKey ? (
        <VanKhiLichNgayPanel
          part="summary"
          date={activeKey}
          onSelectDate={(iso) => setSelectedKey(iso)}
        />
      ) : null}

      {kind === "day" ? (
        <h2 className="pt-2 font-display text-lg font-bold text-foreground">Bài luận của thầy</h2>
      ) : null}

      {readingQuery.isLoading ? (
        <Skeleton className="h-56 w-full rounded-3xl" />
      ) : readingQuery.data?.reading ? (
        <VanKhiReadingView
          kind={kind}
          periodKey={readingQuery.data.period.key}
          reading={readingQuery.data.reading}
          withAlmanac={kind === "day"}
        />
      ) : readingQuery.data ? (
        <VanKhiGate
          kind={kind}
          period={readingQuery.data.period}
          serverNow={serverNow}
          nextOpenAt={readingQuery.data.next_open_at ?? periodsQuery.data?.next_open_at ?? null}
          opening={openMutation.isPending}
          onOpen={() => openMutation.mutate()}
          onPrevious={() => previous && setSelectedKey(previous.key)}
          canGoPrevious={Boolean(previous)}
          canPurchase={readingQuery.data.can_purchase}
          price={readingQuery.data.price}
        />
      ) : (
        <p className="py-10 text-center text-sm text-muted-foreground">
          Không đọc được kỳ này. Thử chọn kỳ khác nhé.
        </p>
      )}
    </div>
  );
}
